import type { Gear, InputFrame, LightsMode, Transmission } from '../sim/types';
import { NEUTRAL_INPUT, isForwardGear } from '../sim/types';
import type { InputAction, InputSource } from './InputSource';
import { KeyboardSource } from './KeyboardSource';
import { GamepadSource } from './GamepadSource';
import { TouchSource } from './TouchSource';
import { clutchCruiseReleaseStep, clutchPressStep, clutchReleaseStep } from './clutchProfile';

const AUTO_GEAR_ORDER: Gear[] = ['P', 'R', 'N', 'D'];

export interface InputManagerOptions {
  mode: Transmission;
  /** Marchas hacia delante de la caja (manual). */
  gearCount: number;
}

/**
 * Combina todas las fuentes de entrada y mantiene el estado del "habitáculo":
 * selector solicitado, intermitentes, luces, cinturón, freno de mano… y el
 * PEDAL DE EMBRAGUE virtual, integrado aquí para que teclado, mando y táctil
 * compartan la misma física de pedal.
 */
export class InputManager {
  private sources: InputSource[];
  /** Acciones que la UI debe atender (cámara, pausa). */
  onUiAction: ((a: InputAction) => void) | null = null;
  /** Notificación de cambios de estado audibles (intermitente, claxon). */
  onCabinAction: ((a: InputAction) => void) | null = null;

  private readonly mode: Transmission;
  private readonly manualGearOrder: Gear[];
  private requestedGear: Gear;
  private signalLeft = false;
  private signalRight = false;
  private lights: LightsMode = 0;
  private seatbelt = false;
  private handbrake = true; // el coche empieza aparcado
  private horn = false;
  private ignition = false;

  /** Pedal de embrague integrado (0 suelto, 1 pisado). */
  private clutchPedal = 0;
  /** La última pisada vino de un control momentáneo (mando). */
  private lastPressMomentary = false;
  /** Velocidad del último tick, para la suelta cómoda en marcha. */
  private speedFeedbackMs = 0;

  constructor(
    opts: InputManagerOptions,
    sources?: InputSource[],
    readonly touchSource = new TouchSource(),
  ) {
    this.mode = opts.mode;
    this.requestedGear = opts.mode === 'manual' ? 'N' : 'P';
    const fwd: Gear[] = [];
    for (let g = 1; g <= Math.min(6, opts.gearCount); g++) fwd.push(g as Gear);
    this.manualGearOrder = ['R', 'N', ...fwd];
    this.sources = sources ?? [new KeyboardSource(), new GamepadSource(), touchSource];
  }

  /** El bucle de juego informa de la velocidad para la suelta adaptativa. */
  setSpeedFeedback(speedMs: number): void {
    this.speedFeedbackMs = speedMs;
  }

  /** Muestrea un tick de simulación (dt fijo). */
  sample(dt: number): InputFrame {
    let throttle = 0;
    let brake = 0;
    let steer = 0;
    let glance: InputFrame['glance'] = 'none';
    let clutchAnalog = 0;
    let hold = false;
    let feed = false;
    let momentary = false;

    for (const src of this.sources) {
      const s = src.sample(dt);
      throttle = Math.max(throttle, s.throttle);
      brake = Math.max(brake, s.brake);
      if (Math.abs(s.steer) > Math.abs(steer)) steer = s.steer;
      if (s.glance !== 'none') glance = s.glance;
      clutchAnalog = Math.max(clutchAnalog, s.clutchAnalog);
      hold = hold || s.clutchHold;
      feed = feed || s.clutchFeed;
      momentary = momentary || s.clutchMomentary;
      for (const a of src.drainActions()) this.applyAction(a);
    }

    const clutch = this.mode === 'manual'
      ? this.integrateClutch(dt, clutchAnalog, hold, feed, momentary)
      : 0;

    return {
      ...NEUTRAL_INPUT,
      throttle,
      brake,
      clutch,
      steer,
      gear: this.requestedGear,
      ignition: this.mode === 'manual' && this.ignition,
      handbrake: this.handbrake,
      signalLeft: this.signalLeft,
      signalRight: this.signalRight,
      lights: this.lights,
      horn: this.horn,
      seatbelt: this.seatbelt,
      glance,
    };
  }

  /**
   * Física del pedal virtual:
   *  - pisar (Shift/X) sube rápido;
   *  - con Shift, al soltar la tecla el pedal SE QUEDA y se suelta con Ctrl
   *    (graduado), que es donde vive la habilidad del punto de fricción;
   *  - con X de mando, al soltar cae solo con el perfil graduado;
   *  - en marcha (>4 m/s) la suelta es directa: comodidad al cambiar;
   *  - un eje analógico (deslizador táctil / pedal real) manda sobre todo.
   */
  private integrateClutch(
    dt: number,
    analog: number,
    hold: boolean,
    feed: boolean,
    momentary: boolean,
  ): number {
    if (hold || momentary) {
      this.clutchPedal = clutchPressStep(this.clutchPedal, dt);
      this.lastPressMomentary = momentary && !hold;
    } else if (feed) {
      this.clutchPedal = clutchReleaseStep(this.clutchPedal, dt);
    } else if (this.clutchPedal > 0) {
      if (Math.abs(this.speedFeedbackMs) > 4) {
        this.clutchPedal = clutchCruiseReleaseStep(this.clutchPedal, dt);
      } else if (this.lastPressMomentary) {
        this.clutchPedal = clutchReleaseStep(this.clutchPedal, dt);
      }
      // Con Shift a baja velocidad, el pedal se mantiene donde está.
    }
    return Math.max(this.clutchPedal, analog);
  }

  private acceptGear(g: Gear): boolean {
    if (this.mode === 'manual') {
      return g === 'R' || g === 'N' || (isForwardGear(g) && this.manualGearOrder.includes(g));
    }
    return g === 'P' || g === 'R' || g === 'N' || g === 'D';
  }

  private applyAction(a: InputAction): void {
    switch (a.type) {
      case 'gear':
        if (this.acceptGear(a.gear)) this.requestedGear = a.gear;
        break;
      case 'gearCycle': {
        const order = this.mode === 'manual' ? this.manualGearOrder : AUTO_GEAR_ORDER;
        const idx = order.indexOf(this.requestedGear);
        const next = order[Math.min(order.length - 1, Math.max(0, idx + a.dir))];
        if (next !== undefined) this.requestedGear = next;
        break;
      }
      case 'signal':
        if (a.side === 'left') {
          this.signalLeft = !this.signalLeft;
          this.signalRight = false;
        } else {
          this.signalRight = !this.signalRight;
          this.signalLeft = false;
        }
        this.onCabinAction?.(a);
        break;
      case 'lights':
        this.lights = ((this.lights + 1) % 3) as LightsMode;
        break;
      case 'belt':
        this.seatbelt = !this.seatbelt;
        break;
      case 'handbrake':
        this.handbrake = !this.handbrake;
        break;
      case 'hornDown':
        this.horn = a.down;
        this.onCabinAction?.(a);
        break;
      case 'ignitionDown':
        this.ignition = a.down;
        break;
      case 'camera':
      case 'pause':
        this.onUiAction?.(a);
        break;
    }
  }

  /** Sincroniza el selector con la marcha real (tras un rechazo del bloqueo). */
  syncGear(actual: Gear): void {
    this.requestedGear = actual;
  }

  cancelSignal(side: 'left' | 'right'): void {
    if (side === 'left') this.signalLeft = false;
    else this.signalRight = false;
  }

  get beltOn(): boolean {
    return this.seatbelt;
  }

  dispose(): void {
    for (const s of this.sources) s.dispose();
  }
}
